import React, { useState } from 'react';
import ReactJson from 'react-json-view';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { EMAIL_REG_EXP } from 'lib/validations';
import { Button } from 'ui';
import { SIGNIN_FORM_INITIAL_STATE, FormFieldsTypes, ISignInFields, SignInInputTypes } from '../model/SignIn.model';
import { useStyles } from './SignInForm.styles';
import { signIn } from '../api/SignIn.client';

export function SignInForm() {
    const classes = useStyles();
    const [inputType, setInputType] = useState<string>(SignInInputTypes.Password);
    const [isPasswordShow, setIsPasswordShow] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<Nullable<any>>(null);
    const [successResponse, setSuccessResponse] = useState<Nullable<any>>(null);

    const { register, handleSubmit, formState: { errors, isSubmitting, isSubmitted } } = useForm<ISignInFields>({
        mode: 'onChange',
        defaultValues: SIGNIN_FORM_INITIAL_STATE,
    });

    const signInEmailRegister = register(FormFieldsTypes.Email, {
        required: 'Email is required',
        pattern: {
            value: EMAIL_REG_EXP,
            message: 'Email is required',
        },
        // validate: (v) => isCorrectEmail(v),
    });

    const signInPasswordRegister = register(FormFieldsTypes.Password, {
        required: 'Password is required',
        // pattern: {
        //     value: PASSWORD_REG_EXP,
        //     message: 'Your password should contain an upper and a lower case alphabet, a numeric and a special character',
        // },
        maxLength: {
            value: 30,
            message: 'Maximum 30 characters',
        },
    });

    const handleClickShowPassword = () => {
        setInputType(isPasswordShow ? SignInInputTypes.Password : SignInInputTypes.Input);
        setIsPasswordShow(!isPasswordShow);
    };

    const handleFormSubmit: SubmitHandler<ISignInFields> = async (params) => {
        try {
            const { data } = await signIn(params);
            setSuccessResponse(data);
        } catch (error: any) {
            setErrorMessage({
                response: error?.response || {},
                request: error?.request || {},
            });
        }
    };

    const errorNode = (error) => (
        <>
            <span className={classes.infoIcon}><AiOutlineInfoCircle /></span>
            <span className={classes.errorMessage}>{error}</span>
        </>
    );

    return (
        <Box
            sx={{
                marginTop: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
                Sign in
            </Typography>
            <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate sx={{ mt: 1 }}>
                <TextField
                    {...signInEmailRegister}
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    helperText={isSubmitted && !!errors[FormFieldsTypes.Email] && errorNode(errors?.[FormFieldsTypes.Email]?.message)}
                    error={isSubmitted && !!errors[FormFieldsTypes.Email]}
                />
                <TextField
                    {...signInPasswordRegister}
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    id="password"
                    autoComplete="current-password"
                    helperText={isSubmitted && errors?.[FormFieldsTypes.Password]?.message && errorNode(errors?.[FormFieldsTypes.Password]?.message)}
                    error={isSubmitted && !!errors[FormFieldsTypes.Password]}
                    type={inputType}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment className={classes.visibilityButton} position="end" onClick={handleClickShowPassword}>
                                {isPasswordShow ? <Visibility /> : <VisibilityOff />}
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    isLoading={isSubmitting}
                >
                    Sign In
                </Button>
                <Grid container>
                    <Grid item xs />
                    <Grid item>
                        <Link to="/sign-up">
                            Don`t have an account? Sign Up
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            {successResponse && (
                <ReactJson src={successResponse} collapsed={1} theme="monokai" />
            )}

            {errorMessage && (
                <ReactJson src={errorMessage} collapsed={1} theme="monokai" style={{ maxWidth: 500 }} />
            )}
        </Box>
    );
}
